import { AdmPlan } from "@custom-types/base-types";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { ReactNode, useState } from "react";
import { Box } from "@mui-wrapper";
import {
  DataGridCell,
  getEntity,
} from "@components/common/data-grid/adm-data-grid-utils";
import { useAppSelector } from "@store/store-helper";
import {
  sceneSeatsLimitSelector,
  activeMembersLimitSelector,
} from "@store/plans/plans-selector";
import { Button, Popover, useToast, WarningIcon } from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SupportEvents } from "@utils/track-event/track-event-list";
import {
  getSceneSeatConstraint,
  SCENE_SEATS_CANNOT_EXCEED_PLAN_LIMIT_ERROR,
} from "@utils/plan-utils";
import { formatLimitType } from "@utils/limit-data-display";

/** Return plan limit cell in data-grid */
export function DataGridSceneSeatCell(
  params: EntityCellParams<AdmPlan>
): ReactNode {
  const plan = getEntity(params);
  const sceneSeatsLimit = useAppSelector(sceneSeatsLimitSelector(plan.id));
  const activeMemberLimit = useAppSelector(activeMembersLimitSelector(plan.id));
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();

  const maxActiveMembers = activeMemberLimit?.max ?? 0;

  // A SCENE_SEAT constraint is listed with the other limits of the plan, so the seats are only
  // counted from the attached SCENE seat plans for the plans created before that constraint
  const hasSceneSeatConstraint = getSceneSeatConstraint(plan) !== undefined;

  if (!sceneSeatsLimit || hasSceneSeatConstraint) {
    return null;
  }

  /**
   * Triggers an Amplitude event to report the issue and shows a toast notification to the user.
   */
  function reportIssue(): void {
    trackEvent({
      name: SupportEvents.reportSceneLicenseIssue,
      props: {
        maxActiveMembers,
        usedSceneSeats: sceneSeatsLimit?.usedSeats,
        totalSceneSeats: sceneSeatsLimit?.totalSeats,
        planId: plan.id,
      },
    });
    openToast({
      title: "Thank you for reporting the issue",
      variant: "success",
    });
  }

  return (
    <Box component="div" sx={{ display: "flex", alignItems: "center" }}>
      <Box component="div">
        <DataGridCell
          key="scene-sceats-limit"
          content={`${sceneSeatsLimit?.usedSeats} | ${
            sceneSeatsLimit?.totalSeats
          } ${formatLimitType("SCENE_SEAT", sceneSeatsLimit?.totalSeats)}`}
        />
      </Box>
      {sceneSeatsLimit?.totalSeats > maxActiveMembers && (
        <Box component="div">
          <WarningIcon
            sx={{ color: "warning.main", ml: "4px", height: "20px" }}
            onMouseEnter={(event) => {
              setAnchorEl(event.currentTarget);
              setIsPopoverOpen(true);
            }}
          />
          <Popover
            open={isPopoverOpen}
            anchorEl={anchorEl}
            closeOnClickOutside
            onClose={() => {
              setIsPopoverOpen(false);
              setAnchorEl(null);
            }}
            title={SCENE_SEATS_CANNOT_EXCEED_PLAN_LIMIT_ERROR}
            description={
              <>
                <Box component="div" sx={{ mb: "10px" }}>
                  Please contact an AdminPanel administrator to fix this issue
                </Box>
                <Button onClick={reportIssue}>Report Problem</Button>
              </>
            }
          />
        </Box>
      )}
    </Box>
  );
}
